import React, {
  ReactElement,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {Alert, Button} from '@customink/pigment-react';
import {skipToken} from '@reduxjs/toolkit/query/react';
import Skeleton from '@mui/material/Skeleton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import {selectAdmin} from '../../slices/userSlice';
import {useAppSelector} from '../../hooks';
import {useFetchProductsQuery} from '../../api/productsApi';
import StoreProductList from './StoreProductList';
import {useGetStoreProductsQuery} from '../../api/storesApi';
import StoreProductEmpty from '../StoreProductsEmpty';
import {useStoreType} from '../../hooks/useStoreType';
import {MobileContext} from '../../containers/MobileProvider';
import StoreProductCardList from './StoreProductCardList';
import services from '../../api/remoteServices';
import './index.scss';

type StoreProductsProps = {
  storeSlug: string;
};

type AddProductsEvent = {
  event: 'add_products' | 'add_products_initiate' | 'add_products_success';
  status: 'internal' | 'external';
  id: string;
};

function ProductListSkeletonRow() {
  return (
    <TableRow className="productRow">
      <TableCell className="productTableCell" />
      <TableCell className="productTableCell">
        <div className="productInfo">
          <div className="productImageContainer">
            <Skeleton variant="rectangular" width={80} height={80} />
          </div>
          <div className="productInfoItem">
            <Skeleton variant="text" width={200} />
          </div>
        </div>
      </TableCell>
      <TableCell align="right">
        <Skeleton variant="text" />
      </TableCell>
      <TableCell align="left">
        <Skeleton variant="text" />
      </TableCell>
      <TableCell className="productActionCell" />
    </TableRow>
  );
}

function ProductListSkeleton() {
  return (
    <>
      <div className="storeProducts-actionBar">
        <Skeleton>
          <Button size="large">Placeholder text</Button>
        </Skeleton>
      </div>
      <TableContainer component={Paper} className="productsList">
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow sx={{fontWeight: 'bold'}}>
              <TableCell />
              <TableCell align="left">
                <Skeleton variant="text" width={100} />
              </TableCell>
              <TableCell align="left">
                <Skeleton variant="text" />
              </TableCell>
              <TableCell align="left">
                <Skeleton variant="text" />
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            <ProductListSkeletonRow />
            <ProductListSkeletonRow />
            <ProductListSkeletonRow />
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

const StoreProducts: React.FC<StoreProductsProps> = ({
  storeSlug
}: StoreProductsProps): ReactElement => {
  const [addedProductsQty, setAddedProductsQty] = useState(null);
  const history = useHistory();
  const location = useLocation();
  const isInitialMount = useRef(true);
  const publishAddProductsEventRef = useRef<(event: AddProductsEvent) => void>(
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    () => {}
  );
  const isAdmin = useAppSelector(selectAdmin);
  const {data: storeProductResults} = useGetStoreProductsQuery(storeSlug);
  const {isStoreTypeSelfServe} = useStoreType();
  useFetchProductsQuery(
    storeProductResults
      ? {ids: storeProductResults.map(p => p.styleId)}
      : skipToken
  );
  const mmsProductResults = useAppSelector(state => state.stores.mmsProducts);
  const storeDetails = useAppSelector(state => state.stores.storeDetails);
  const mobile = useContext(MobileContext);

  const publishAddProductsEvent = (addProductsEvent: AddProductsEvent) => {
    publishAddProductsEventRef.current(addProductsEvent);
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const addedProductsQtyParam = searchParams.get('added_products_qty');

    if (addedProductsQtyParam !== null) {
      setAddedProductsQty(addedProductsQtyParam);

      searchParams.delete('added_products_qty');
      const newSearch = searchParams.toString();
      const newPath = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;

      history.replace(newPath);
    }
  }, [location, history]);

  useEffect(() => {
    publishAddProductsEventRef.current = (
      addProductsEvent: AddProductsEvent
    ) => {
      if (window.dataLayer && Array.isArray(window.dataLayer)) {
        window.dataLayer.push(addProductsEvent);
      }
    };
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      publishAddProductsEvent({
        event: 'add_products',
        status: isAdmin ? 'internal' : 'external',
        id: storeSlug
      });
    }
  }, []);

  const handleAddProductClick = () => {
    if (storeDetails?.eventStore && storeDetails?.addFromCatalog) {
      let redirectUrl = `${
        services.stores
      }/${storeSlug}/admin/products/new?redirect_url=${encodeURIComponent(
        `${services.storefront}/${storeSlug}/manage/products`
      )}&compatible_type_only=true&quote=true`;

      if (storeDetails.designCompositeId) {
        redirectUrl += `&cid=${storeDetails.designCompositeId}`;
      }

      publishAddProductsEvent({
        event: 'add_products_initiate',
        status: isAdmin ? 'internal' : 'external',
        id: storeSlug
      });

      window.location.href = redirectUrl;
    } else {
      history.push(`/${storeSlug}/products/new`);
    }
  };

  const storeProductsContainerClasses = `storeProducts${
    mobile ? ' mobile' : ''
  }`;

  const showAddAdditionalProductsButton = (): boolean => {
    if (!storeDetails) return false;

    const isSelfServeNonWaveStore =
      isStoreTypeSelfServe && storeDetails.fulfillVia !== 'wave';

    const isEventStoreWithAddFromCatalogFlag =
      storeDetails.eventStore && storeDetails.addFromCatalog;

    return isSelfServeNonWaveStore || isEventStoreWithAddFromCatalogFlag;
  };

  return (
    <div className="storeProductPanel">
      <div className={storeProductsContainerClasses}>
        {addedProductsQty && (
          <Alert severity="success">
            {addedProductsQty} product
            {addedProductsQty !== '1' ? 's have ' : ' has '}
            been successfully added to your Group Order Form.
          </Alert>
        )}
        {!storeProductResults && <ProductListSkeleton />}
        {storeProductResults &&
          (storeProductResults.length > 0 ? (
            <>
              <div className="storeProducts-actionBar">
                <h2>Your Products</h2>
                {showAddAdditionalProductsButton() && (
                  <Button
                    size="large"
                    variant="secondary"
                    onClick={handleAddProductClick}>
                    Add Additional Products
                  </Button>
                )}
              </div>
              {mobile ? (
                <StoreProductCardList
                  products={storeProductResults}
                  mmsProducts={mmsProductResults}
                />
              ) : (
                <StoreProductList
                  products={storeProductResults}
                  mmsProducts={mmsProductResults}
                  storeDetails={storeDetails}
                  storeSlug={storeSlug}
                  setAddedProductsQty={setAddedProductsQty}
                />
              )}
            </>
          ) : (
            <StoreProductEmpty storeSlug={storeSlug} />
          ))}
      </div>
    </div>
  );
};

export default StoreProducts;
